import { useCallback, useMemo, useState, type PropsWithChildren } from 'react';
import { AppNoticesContext } from './app-notices-context';
import type {
  AppNotice,
  AppNoticeRegistration,
} from '../../types/notices/app-notice.type';
import { selectAppNotice } from '../../utils/notices/select-app-notice.util';

/**
 * Owns transient notice registrations for one authenticated application shell.
 * @param props - Descendants containing producers and the notice region.
 * @returns The shared notice context provider.
 */
export const AppNoticesProvider = ({ children }: PropsWithChildren) => {
  const [registrations, setRegistrations] = useState<
    Map<string, AppNoticeRegistration>
  >(() => new Map());
  const registerNotice = useCallback((notice: AppNotice) => {
    const token = Symbol(notice.id);
    setRegistrations((current) =>
      new Map(current).set(notice.id, { notice, token })
    );
    return () => {
      setRegistrations((current) => {
        if (current.get(notice.id)?.token !== token) return current;
        const next = new Map(current);
        next.delete(notice.id);
        return next;
      });
    };
  }, []);
  const removeNotice = useCallback((id: string) => {
    setRegistrations((current) => {
      if (!current.has(id)) return current;
      const next = new Map(current);
      next.delete(id);
      return next;
    });
  }, []);
  const value = useMemo(
    () => ({
      activeNotice: selectAppNotice(
        Array.from(registrations.values(), ({ notice }) => notice)
      ),
      registerNotice,
      removeNotice,
    }),
    [registrations, registerNotice, removeNotice]
  );

  return (
    <AppNoticesContext.Provider value={value}>
      {children}
    </AppNoticesContext.Provider>
  );
};
